import { Injectable } from '@angular/core';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { InventoryCheckboxFilterModel } from 'src/app/models/results/inventory-checkbox-filter';
import { InventoryGroup, InventoryGroupName } from 'src/app/models/results/inventory-group';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { SharedFilteringService } from '../shared-filtering.service';
import { DataFilteringService } from 'src/app/models/interfaces/data-filtering-service';
import { PriceSort } from 'src/app/models/results/price-sort';

@Injectable({
  providedIn: 'root'
})
export class EnterpriseFilteringService implements DataFilteringService {
  readonly NUMBER_OF_ROUNDS: number = 4;

  constructor(private sharedFilteringService: SharedFilteringService) {}

  sortItems(items: InventoryItemDTO[], queryParams: InventoryQueryParams, priceSort?: PriceSort): InventoryItemDTO[] {
    if (priceSort) {
      return this.sharedFilteringService.sortByPriceSortDirection(items, priceSort);
    }

    return this.runDefaultSorting(items, queryParams);
  }

  runDefaultSorting(items: InventoryItemDTO[], queryParams: InventoryQueryParams): InventoryItemDTO[] {
    if (queryParams.groups.length === 1 && queryParams.groups[0].name === InventoryGroupName.BrowsingAndSingleTasks) {
      return this.sharedFilteringService.sortByPriceLowToHigh(items);
    }

    return this.sharedFilteringService.sortByPriceHighToLow(items);
  }

  getRoundCount(): number {
    return this.NUMBER_OF_ROUNDS;
  }

  applyFilteringByRound(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    round: number,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
    switch (round) {
      case 1:
        return this.applyRound1Filtering(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
      case 2:
        return this.applyRound2Filtering(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
      case 3:
        return this.applyRound3Filtering(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
      case 4:
        return this.applyRound4Filtering(itemsToFilter, queryParams, group, filterItemsFromCheckboxParams);
      default:
        return itemsToFilter;
    }
  }

  applyRound1Filtering(itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      if (group.parallelsSelected) {
        itemsToFilter = this.sharedFilteringService.filterByParallels(itemsToFilter);
        itemsToFilter = this.sharedFilteringService.filterByBrand(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
        itemsToFilter = this.sharedFilteringService.filterByCPU(itemsToFilter, group);
        itemsToFilter = this.sharedFilteringService.filterByMemory(itemsToFilter, group);
        itemsToFilter = this.sharedFilteringService.filterByStorage(itemsToFilter, queryParams, group, true, filterItemsFromCheckboxParams);
        itemsToFilter = this.sharedFilteringService.filterByScreenSizeCheckboxes(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
        itemsToFilter = this.sharedFilteringService.filterByFormFactorCheckboxes(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
        itemsToFilter = this.sharedFilteringService.filterByFeatureCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
        itemsToFilter = this.sharedFilteringService.filterByOsTypeCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);

        return itemsToFilter;
      }

      return [];
  }

  applyRound2Filtering(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      itemsToFilter = this.sharedFilteringService.filterByBrand(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByCPU(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByMemory(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByScreenSize(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByStorage(itemsToFilter, queryParams, group, false, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFormFactor(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByBacklitKeyboard(itemsToFilter, queryParams);
      itemsToFilter = this.sharedFilteringService.filterByFingerprintSensor(itemsToFilter, queryParams);
      itemsToFilter = this.sharedFilteringService.filterByPrivacyScreen(itemsToFilter, queryParams);
      itemsToFilter = this.sharedFilteringService.filterByStylus(itemsToFilter, queryParams);
      itemsToFilter = this.sharedFilteringService.filterByFeatureCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByOsTypeCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);

      return itemsToFilter;
  }

  applyRound3Filtering(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      itemsToFilter = this.sharedFilteringService.filterByBrand(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByCPU(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByMemory(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByScreenSize(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByStorage(itemsToFilter, queryParams, group, false, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFormFactor(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFeatureCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByOsTypeCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);

      return itemsToFilter;
  }

  applyRound4Filtering(
    itemsToFilter: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): InventoryItemDTO[] {
      itemsToFilter = this.sharedFilteringService.filterByBrand(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByCPU(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByMemory(itemsToFilter, group);
      itemsToFilter = this.sharedFilteringService.filterByStorage(itemsToFilter, queryParams, group, false, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFormFactor(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByScreenSizeCheckboxes(itemsToFilter, queryParams, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByFeatureCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);
      itemsToFilter = this.sharedFilteringService.filterByOsTypeCheckboxes(itemsToFilter, filterItemsFromCheckboxParams);

      return itemsToFilter;
  }
}

